import React, { useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { SignInPage } from './SignInPage';
import { SignUpPage } from './SignUpPage';
import { OrganizationRouter } from '../OrganizationRouter';
import OnboardingPage from '../OnboardingPage';
import { TemplateApp } from '../TemplateApp';

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-light-bg-primary dark:bg-dark-bg-primary flex items-center justify-center p-4">
      <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-2xl shadow-xl p-8 w-full max-w-md border border-light-border dark:border-dark-border">
        <h2 className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary mb-4">
          Forgot Password
        </h2>
        <p className="text-light-text-secondary dark:text-dark-text-secondary mb-6">
          Password reset functionality coming soon!
        </p>
        <button
          onClick={() => navigate('/signin')}
          className="w-full bg-primary-500 text-white py-3 rounded-lg hover:bg-primary-600 transition-colors"
        >
          Back to Sign In
        </button>
      </div>
    </div>
  );
};

export const AuthRouter: React.FC = () => {
  const { isAuthenticated, isLoading, getCurrentOrganizationId } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Check if current path is org-based URL (for templates)
  const isOrgBasedUrl = /^\/org_[^\/]+\//.test(location.pathname);

  // Handle old template routes (redirect to org-based URLs if authenticated)
  useEffect(() => {
    if (location.pathname.startsWith('/templates')) {
      if (isAuthenticated && !isOrgBasedUrl) {
        // Redirect old template URLs to org-based URLs
        const currentOrgId = getCurrentOrganizationId();
        navigate(`/${currentOrgId}${location.pathname}`);
      }
    }
  }, [location.pathname, isAuthenticated, isOrgBasedUrl, getCurrentOrganizationId, navigate]);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-light-bg-primary dark:bg-dark-bg-primary flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
          <p className="text-light-text-secondary dark:text-dark-text-secondary">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  // Always allow public sign-up route even if already authenticated
  if (location.pathname === '/signup') {
    return (
      <SignUpPage
        onSignUpSuccess={() => navigate('/signin')}
        onSwitchToSignIn={() => navigate('/signin')}
      />
    );
  }

  // Check if we're on an external route (always standalone, regardless of auth)
  if (location.pathname.startsWith('/external')) {
    return <TemplateApp />;
  }

  // Check if we're on a public demo route (always public, no auth required)
  if (location.pathname.startsWith('/public/demo')) {
    return <TemplateApp />;
  }

  // Check if we're on a public video-connect route (always public, no auth required)
  if (location.pathname.startsWith('/public/video-connect')) {
    return <TemplateApp />;
  }

  // Check if URL starts with an org ID pattern (org_xxxxx)
  const orgIdMatch = location.pathname.match(/^\/([a-zA-Z0-9_]+)/);
  const urlOrgId = orgIdMatch ? orgIdMatch[1] : null;

  // SECURITY FIX: Templates should require authentication
  // Define public template paths (both old and new format)
  const publicTemplatePaths = [
    '/templates/demo',
    '/templates/preview',
    '/templates/legal/client',
    '/templates/legal/demo',
    '/templates/legal/preview',
    '/templates/legal/session/participant',  // Add legal customer video session as public
    '/templates/support/customer',
    '/templates/support/waiting',  // Add support waiting room as public
    '/templates/support/session/customer',  // Add support customer video session as public
    '/templates/support/demo', 
    '/templates/support/preview',
    '/templates/homeimprovement/customer',
    '/templates/homeimprovement/waiting',  // Add home improvement waiting room as public
    '/templates/homeimprovement/session/customer',  // Add home improvement customer video session as public
    '/templates/homeimprovement/demo',
    '/templates/homeimprovement/preview',
    '/templates/universal/demo',
    '/templates/universal/preview',
    '/templates/universal/waiting',  // Add waiting room as public
    '/templates/universal/session',  // Add video session as public
    '/templates/universal/request-under-review',  // Add rejection page as public
    '/templates/universal/request-form',  // Add request form as public
    '/templates/universal/session-ended',  // Add session ended page as public
    '/templates/general/demo',           // Add general demo form as public
    '/templates/general/session/participant', // Add general participant session as public
    '/public/demo',                       // Public demo landing page
    '/public/demo/host',                  // Public demo host interface
    '/public/demo/guest',                 // Public demo guest interface
    '/public/demo/limit-reached',         // Demo limit conversion page
    '/public/demo/monitor',               // Public demo monitoring dashboard
    '/templates/healthcare/demo',
    '/templates/healthcare/preview',
    '/templates/education/demo',
    '/templates/education/preview',
    '/templates/interview/demo',
    '/templates/interview/preview',
    '/templates/sales/demo',
    '/templates/sales/preview',
    // Standalone template routes (without /templates prefix)
    '/legal/preview',
    '/legal/demo',
    '/support/preview',
    '/support/demo',
    '/homeimprovement/preview',
    '/homeimprovement/demo',
    '/universal/preview',
    '/universal/demo',
    '/universal/waiting',  // Add waiting room as public
    '/universal/session',  // Add video session as public
    '/universal/request-under-review',  // Add rejection page as public
    '/universal/request-form',  // Add request form as public
    '/universal/session-ended',  // Add session ended page as public
    '/public/demo',              // Public demo landing page
    '/public/demo/host',         // Public demo host interface
    '/public/demo/guest',        // Public demo guest interface
    '/public/demo/limit-reached', // Demo limit conversion page
    '/healthcare/preview',
    '/healthcare/demo',
    '/education/preview',
    '/education/demo',
    '/interview/preview',
    '/interview/demo',
    '/sales/preview',
    '/sales/demo',
    // New org-based public paths
    '/*/templates/legal/client',
    '/*/templates/legal/demo',
    '/*/templates/legal/preview',
    '/*/templates/support/customer',
    '/*/templates/support/demo',
    '/*/templates/support/preview',
    '/*/templates/homeimprovement/customer',
    '/*/templates/homeimprovement/demo',
    '/*/templates/homeimprovement/preview',
    '/*/templates/universal/demo',
    '/*/templates/universal/preview',
    '/*/templates/universal/waiting',  // Add waiting room as public
    '/*/templates/universal/session',  // Add video session as public
    '/*/templates/universal/request-under-review',  // Add rejection page as public
    '/*/templates/universal/request-form',  // Add request form as public
    '/*/templates/universal/session-ended',  // Add session ended page as public
    '/*/templates/general/demo',             // Add general demo form as public (org-based)
    '/*/templates/general/session/participant', // Add general participant session as public (org-based)
    '/*/public/demo',                        // Public demo landing page (org-based)
    '/*/public/demo/host',                   // Public demo host interface (org-based)
    '/*/public/demo/guest',                  // Public demo guest interface (org-based)
    '/*/public/demo/limit-reached',          // Demo limit conversion page (org-based)
    '/*/templates/healthcare/demo',
    '/*/templates/healthcare/preview',
    '/*/templates/education/demo',
    '/*/templates/education/preview',
    '/*/templates/interview/demo',
    '/*/templates/interview/preview',
    '/*/templates/sales/demo',
    '/*/templates/sales/preview',
    // Org-based standalone routes
    '/*/legal/preview',
    '/*/legal/demo',
    '/*/support/preview', 
    '/*/support/demo',
    '/*/homeimprovement/preview',
    '/*/homeimprovement/demo',
    '/*/universal/preview',
    '/*/universal/demo',
    '/*/universal/waiting',  // Add waiting room as public
    '/*/universal/session',  // Add video session as public
    '/*/universal/request-under-review',  // Add rejection page as public
    '/*/universal/request-form',  // Add request form as public
    '/*/universal/session-ended',  // Add session ended page as public
    // Org-based template routes with dynamic IDs
    '/*/templates/support/customer',
    '/*/templates/support/waiting',  // Support waiting room with org prefix
    '/*/templates/support/waiting/*',  // Support waiting room with ticket ID
    '/*/templates/support/session/customer',  // Support customer video session with org prefix
    '/*/templates/homeimprovement/customer',
    '/*/templates/homeimprovement/waiting',  // Home improvement waiting room with org prefix
    '/*/templates/homeimprovement/waiting/*',  // Home improvement waiting room with project ID
    '/*/templates/homeimprovement/session/customer',  // Home improvement customer video session with org prefix
    '/*/templates/legal/client',
    '/*/templates/legal/demo',
    '/*/templates/legal/session/participant',  // Legal customer video session with org prefix
    '/*/public/demo',              // Public demo landing page (org standalone)
    '/*/public/demo/host',         // Public demo host interface (org standalone)
    '/*/public/demo/guest',        // Public demo guest interface (org standalone)
    '/*/public/demo/limit-reached', // Demo limit conversion page (org standalone)
    '/*/healthcare/preview',
    '/*/healthcare/demo',
    '/*/education/preview',
    '/*/education/demo',
    '/*/interview/preview',
    '/*/interview/demo',
    '/*/sales/preview',
    '/*/sales/demo'
  ];
  
  const isPublicTemplate = publicTemplatePaths.some(path => {
    if (path.includes('*')) {
      // Handle org-based paths with wildcard
      // Replace first * with org ID pattern, subsequent * with catch-all
      const regexPath = path
        .replace(/^\*/, '[a-zA-Z0-9_]+')  // First * matches org ID
        .replace(/\/\*$/, '(?:\/.*)?')   // Trailing /* matches optional path
        .replace(/\*/g, '.*');           // Any other * matches anything
      const regex = new RegExp(`^${regexPath}$`);
      const matches = regex.test(location.pathname);
      
      // Debug logging for path matching
      if (location.pathname.includes('/waiting/') || location.pathname.includes('/support/')) {
        console.log('🔍 [AuthRouter] Path matching debug:', {
          currentPath: location.pathname,
          templatePath: path,
          regexPath,
          matches
        });
      }
      
      return matches;
    }
    return location.pathname.startsWith(path);
  });

  // Debug logging for final public template check
  if (location.pathname.includes('/waiting/') || location.pathname.includes('/support/')) {
    console.log('🔍 [AuthRouter] Final public template check:', {
      currentPath: location.pathname,
      isPublicTemplate,
      isAuthenticated,
      isOrgBasedUrl
    });
  }

  if (location.pathname.startsWith('/templates')) {
    if (isAuthenticated && !isOrgBasedUrl) {
      // Return null while redirecting
      return null;
    }
    
    // Dashboard routes MUST be authenticated
    const dashboardPaths = [
      '/templates/legal/attorney',
      '/templates/support/agent',
      '/templates/homeimprovement/contractor'
    ];
    
    const isDashboard = dashboardPaths.some(path => 
      location.pathname.startsWith(path)
    );
    
    if (isDashboard && !isAuthenticated) {
      return (
        <SignInPage
          onSignInSuccess={() => navigate(location.pathname)}
          onSwitchToSignUp={() => navigate('/signup')}
          onForgotPassword={() => navigate('/forgot-password')}
        />
      );
    }
    
    // Allow public templates or authenticated users
    if (isPublicTemplate || isAuthenticated) {
      return <TemplateApp />;
    }
    
    return (
      <SignInPage
        onSignInSuccess={() => navigate(location.pathname)}
        onSwitchToSignUp={() => navigate('/signup')}
        onForgotPassword={() => navigate('/forgot-password')}
      />
    );
  }

  // Handle org-based URLs
  if (isOrgBasedUrl) {
    // If someone hits /:orgId/signup, normalize to public /signup
    if (location.pathname.endsWith('/signup')) {
      navigate('/signup');
      return null;
    }
    // Check if it's a public template first
    if (isPublicTemplate) {
      return <TemplateApp />;
    }
    
    // For org-based URLs, check authentication
    if (!isAuthenticated) {
      return (
        <SignInPage
          onSignInSuccess={() => {
            // After sign in, redirect to org-based URL or create one
            const currentOrgId = getCurrentOrganizationId();
            const targetPath = location.pathname.replace(`/${urlOrgId}`, `/${currentOrgId}`);
            navigate(targetPath);
          }}
          onSwitchToSignUp={() => navigate('/signup')}
          onForgotPassword={() => navigate('/forgot-password')}
        />
      );
    }
    
    // Authenticated user with org-based URL
    // Use RELATIVE paths because this router is already mounted at /:orgId/*
    return (
      <Routes>
        <Route path={"onboarding"} element={<OnboardingPage />} />
        <Route path={"/*"} element={<OrganizationRouter />} />
      </Routes>
    );
  }

  // If authenticated but no org in URL, redirect to org-based URL
  if (isAuthenticated) {
    const currentOrgId = getCurrentOrganizationId();
    navigate(`/${currentOrgId}${location.pathname || ''}`);
    return null;
  }

  // If not authenticated, show auth pages
  return (
    <Routes>
      <Route 
        path="/signup" 
        element={
          <SignUpPage
            onSignUpSuccess={() => navigate('/signin')}
            onSwitchToSignIn={() => navigate('/signin')}
          />
        } 
      />
      <Route 
        path="/forgot-password" 
        element={<ForgotPasswordPage />} 
      />
      <Route 
        path="*" 
        element={
          <SignInPage
            onSignInSuccess={() => navigate('/')}
            onSwitchToSignUp={() => navigate('/signup')}
            onForgotPassword={() => navigate('/forgot-password')}
          />
        } 
      />
    </Routes>
  );
};